/**
 * m3 echo Frontend - Optimization Tab Component (V3.0)
 *
 * Displays optimization test history and results for a project.
 */

import React from 'react';
import {
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  Alert,
  CircularProgress,
  LinearProgress,
  Tooltip as MuiTooltip,
  IconButton,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Link,
  Divider,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import TrendingDownIcon from '@mui/icons-material/TrendingDown';
import TrendingFlatIcon from '@mui/icons-material/TrendingFlat';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import NatureIcon from '@mui/icons-material/Nature';
import StarIcon from '@mui/icons-material/Star';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import HandshakeIcon from '@mui/icons-material/Handshake';
import VisibilityIcon from '@mui/icons-material/Visibility';
import CampaignIcon from '@mui/icons-material/Campaign';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import DescriptionIcon from '@mui/icons-material/Description';
import EmojiObjectsIcon from '@mui/icons-material/EmojiObjects';
import PlaceIcon from '@mui/icons-material/Place';
import CodeIcon from '@mui/icons-material/Code';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import SourceIcon from '@mui/icons-material/Source';
import SearchIcon from '@mui/icons-material/Search';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import { useOptimizationTests, useCreateOptimizationTest, useRetestOptimization, useCombinedTimeline, useOptimizationQuota } from '../hooks/useOptimization';
import CriterionDetailCard from './CriterionDetailCard';
import TopRecommendationsCard from './TopRecommendationsCard';
import OptimizationRadarChart from './OptimizationRadarChart';
import CombinedTimelineChart from './CombinedTimelineChart';
import { translateStatus } from '../utils/statusTranslations';
import { isV2Test, CATEGORY_ORDER, CATEGORY_CONFIG, getScoreColor, getScoreLabel, V2CategoryKey } from '../utils/scoringUtils';
import { safeText } from '../utils/safeText';
import { AnalysisResultV2, CategoryResultV2 } from '../types/api';
import { useCanEdit } from '../stores/useAuthStore';

export interface OptimizationTabHandle {
  triggerRetest: () => Promise<void>;
}

interface OptimizationTabProps {
  projectId: number;
  homepageUrl?: string | null;
  onConfigureHomepage: () => void;
}

/**
 * Derives cooldown state from the test list (no extra API call needed).
 */
function useCooldownState(tests: any[] | undefined) {
  return React.useMemo(() => {
    if (!tests || tests.length === 0) {
      return { onCooldown: false, inProgress: false, cooldownUntil: null as Date | null };
    }

    const latest = tests[0]; // tests are sorted by created_at DESC

    // In progress
    if (latest.status === 'pending' || latest.status === 'running') {
      return { onCooldown: true, inProgress: true, cooldownUntil: null };
    }

    // Recently completed — 24h cooldown
    if (latest.status === 'completed' && latest.completed_at) {
      const completedAt = new Date(latest.completed_at.endsWith('Z') ? latest.completed_at : latest.completed_at + 'Z');
      const cooldownUntil = new Date(completedAt.getTime() + 24 * 60 * 60 * 1000);
      if (cooldownUntil > new Date()) {
        return { onCooldown: true, inProgress: false, cooldownUntil };
      }
    }

    // Failed or cooldown expired
    return { onCooldown: false, inProgress: false, cooldownUntil: null };
  }, [tests]);
}

function formatCooldownTime(cooldownUntil: Date | null): string {
  if (!cooldownUntil) return '';
  return cooldownUntil.toLocaleString('de-DE', {
    day: '2-digit', month: '2-digit', year: 'numeric',
    hour: '2-digit', minute: '2-digit',
  });
}

function getButtonTooltip(quotaExhausted: boolean, runsUsed: number, runQuota: number, inProgress: boolean, onCooldown: boolean, cooldownUntil: Date | null): string {
  if (inProgress) return 'Test läuft...';
  if (quotaExhausted) return `Kontingent aufgebraucht (${runsUsed}/${runQuota})`;
  if (onCooldown && cooldownUntil) return `Nächster Test möglich ab ${formatCooldownTime(cooldownUntil)}`;
  return `Verfügbar (${runQuota - runsUsed}/${runQuota})`;
}

const OptimizationTab = React.forwardRef<OptimizationTabHandle, OptimizationTabProps>(({
  projectId,
  homepageUrl,
  onConfigureHomepage,
}, ref) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const canEdit = useCanEdit();
  const { data: tests, isLoading, error } = useOptimizationTests(projectId);
  const { data: combinedTimeline } = useCombinedTimeline(projectId);
  const { runQuota, runsUsed, runsRemaining, quotaExhausted } = useOptimizationQuota();
  const cooldown = useCooldownState(tests);
  const createMutation = useCreateOptimizationTest();
  const retestMutation = useRetestOptimization();

  const isButtonDisabled = quotaExhausted || cooldown.onCooldown || createMutation.isPending || retestMutation.isPending;
  const buttonTooltip = getButtonTooltip(quotaExhausted, runsUsed, runQuota, cooldown.inProgress, cooldown.onCooldown, cooldown.cooldownUntil);

  // State for selected test to view details
  const [selectedTestId, setSelectedTestId] = React.useState<number | null>(null);
  const [showAllTests, setShowAllTests] = React.useState(false);

  // State for expanded accordion panels
  const [expandedPanels, setExpandedPanels] = React.useState<Set<string>>(new Set());

  // Default to selecting the first completed test on load
  React.useEffect(() => {
    if (tests && tests.length > 0 && selectedTestId === null) {
      const firstCompletedTest = tests.find(t => t.status === 'completed' && t.overall_score !== null);
      if (firstCompletedTest) {
        setSelectedTestId(firstCompletedTest.id);
      }
    }
  }, [tests, selectedTestId]);

  // Reset expanded panels when selected test changes
  React.useEffect(() => {
    setExpandedPanels(new Set());
  }, [selectedTestId]);

  const togglePanel = (panelId: string) => {
    setExpandedPanels(prev => {
      const newSet = new Set(prev);
      if (newSet.has(panelId)) {
        newSet.delete(panelId);
      } else {
        newSet.add(panelId);
      }
      return newSet;
    });
  };

  /**
   * Formats date for display
   */
  const formatDate = (dateString: string): string => {
    const utcDateString = dateString.endsWith('Z') ? dateString : dateString + 'Z';
    const date = new Date(utcDateString);
    return date.toLocaleDateString('de-DE', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  /**
   * Gets status color
   */
  const getStatusColor = (status: string) => {
    switch (status) {
      case 'completed':
        return 'success';
      case 'running':
        return 'primary';
      case 'failed':
        return 'error';
      case 'pending':
        return 'warning';
      default:
        return 'default';
    }
  };


  /**
   * Gets trend icon
   */
  const getTrendIcon = (currentScore: number | null, previousScore: number | null) => {
    if (previousScore === null || currentScore === null) {
      return <TrendingFlatIcon sx={{ color: 'grey.500' }} />;
    }
    if (currentScore > previousScore) return <TrendingUpIcon sx={{ color: 'success.main' }} />;
    if (currentScore < previousScore) return <TrendingDownIcon sx={{ color: 'error.main' }} />;
    return <TrendingFlatIcon sx={{ color: 'grey.500' }} />;
  };

  /**
   * Handles starting a new optimization test
   */
  const handleStartOptimization = async () => {
    if (!homepageUrl) {
      onConfigureHomepage();
      return;
    }

    try {
      await createMutation.mutateAsync({
        projectId,
        data: { homepage_url: homepageUrl },
      });
    } catch (error) {
      console.error('Failed to create optimization test:', error);
    }
  };

  /**
   * Handles re-testing
   */
  const handleRetest = async () => {
    if (!tests || tests.length === 0) return;

    const latestTest = tests[0];
    await retestMutation.mutateAsync({
      projectId,
      testId: latestTest.id,
    });
  };

  // Expose the retest handler to parent component via ref
  React.useImperativeHandle(ref, () => ({
    triggerRetest: handleRetest,
  }), [handleRetest]);

  // Calculate statistics
  const statistics = React.useMemo(() => {
    if (!tests || tests.length === 0) {
      return {
        latestScore: null,
        totalTests: 0,
        trend: null,
      };
    }

    const completedTests = tests.filter(t => t.status === 'completed' && t.overall_score !== null);
    const latestTest = completedTests[0];

    let trend: 'up' | 'down' | 'stable' | null = null;
    if (completedTests.length >= 2) {
      const previousTest = completedTests[1];
      if (latestTest.overall_score! > previousTest.overall_score!) {
        trend = 'up';
      } else if (latestTest.overall_score! < previousTest.overall_score!) {
        trend = 'down';
      } else {
        trend = 'stable';
      }
    }

    return {
      latestScore: latestTest?.overall_score || null,
      totalTests: tests.length,
      trend,
    };
  }, [tests]);

  // Loading state
  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  // Error state
  if (error) {
    return (
      <Box p={3}>
        <Alert severity="error">
          Optimierungs-Tests konnten nicht geladen werden
        </Alert>
      </Box>
    );
  }

  // Empty state - no homepage URL configured
  if (!homepageUrl) {
    return (
      <Box p={3}>
        <Alert severity="info" sx={{ mb: 3 }}>
          Um Optimierungs-Tests durchzuführen, müssen Sie zuerst eine Homepage-URL konfigurieren.
        </Alert>
        {canEdit && (
          <Button
            variant="contained"
            onClick={onConfigureHomepage}
            startIcon={<AddIcon />}
          >
            Homepage-URL konfigurieren
          </Button>
        )}
      </Box>
    );
  }

  // Empty state - no tests yet
  if (!tests || tests.length === 0) {
    return (
      <Box p={3}>
        <Alert severity="info" sx={{ mb: 3 }}>
          Noch keine Optimierungs-Tests durchgeführt. Starten Sie Ihren ersten Test, um Ihre Web-Präsenz zu analysieren.
        </Alert>

        {canEdit && (
          <Box display="flex" gap={2}>
            <MuiTooltip title={buttonTooltip} arrow>
              <span>
                <Button
                  variant="contained"
                  onClick={handleStartOptimization}
                  disabled={isButtonDisabled}
                  startIcon={<AddIcon />}
                >
                  {createMutation.isPending ? 'Erstelle...' : 'Ersten Test starten'}
                </Button>
              </span>
            </MuiTooltip>
            <Button
              variant="outlined"
              onClick={onConfigureHomepage}
            >
              Homepage-URL ändern
            </Button>
          </Box>
        )}
      </Box>
    );
  }

  return (
    <Box>
      {/* Statistics Cards */}
      <Grid container spacing={{ xs: 1.5, sm: 3 }} mb={3}>
        {!isMobile && (
          <Grid item xs={12} sm={4} md={3}>
            <Card sx={{ height: '100%', '&:hover': { transform: 'translateY(-2px)', boxShadow: 6 } }}>
              <CardContent sx={{ p: { xs: 1.5, sm: 2 } }}>
                <Box display="flex" justifyContent="space-between" alignItems="flex-start">
                  <Box flex={1}>
                    <Typography color="text.secondary" gutterBottom>
                      Homepage URL
                    </Typography>
                    <Typography variant="body1" sx={{ wordBreak: 'break-word' }}>
                      {homepageUrl}
                    </Typography>
                  </Box>
                  {canEdit && (
                    <IconButton
                      size="small"
                      onClick={onConfigureHomepage}
                      sx={{ mt: -1, mr: -1 }}
                    >
                      <EditIcon fontSize="small" />
                    </IconButton>
                  )}
                </Box>
              </CardContent>
            </Card>
          </Grid>
        )}
        <Grid item xs={4} sm={4} md={isMobile ? 4 : 2}>
          <Card sx={{ height: '100%', '&:hover': { transform: 'translateY(-2px)', boxShadow: 6 } }}>
            <CardContent sx={{ p: { xs: 1.5, sm: 2 } }}>
              <Typography color="text.secondary" gutterBottom sx={{ fontSize: { xs: '0.7rem', sm: '0.875rem' } }}>
                Anzahl Tests
              </Typography>
              <Typography sx={{ fontSize: { xs: '1.5rem', sm: '2.125rem' }, fontWeight: 700 }}>
                {statistics.totalTests}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={4} sm={4} md={isMobile ? 4 : 2}>
          <Card sx={{ height: '100%', '&:hover': { transform: 'translateY(-2px)', boxShadow: 6 } }}>
            <CardContent sx={{ p: { xs: 1.5, sm: 2 } }}>
              <Typography color="text.secondary" gutterBottom sx={{ fontSize: { xs: '0.7rem', sm: '0.875rem' } }}>
                Kontingent
              </Typography>
              <Typography sx={{ fontSize: { xs: '1.5rem', sm: '2.125rem' }, fontWeight: 700 }}>
                {runsRemaining} / {runQuota}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={4} sm={4} md={isMobile ? 4 : 2}>
          <Card sx={{ height: '100%', '&:hover': { transform: 'translateY(-2px)', boxShadow: 6 } }}>
            <CardContent sx={{ p: { xs: 1.5, sm: 2 } }}>
              <Typography color="text.secondary" gutterBottom sx={{ fontSize: { xs: '0.7rem', sm: '0.875rem' } }}>
                Trend
              </Typography>
              <Box display="flex" alignItems="center" justifyContent="flex-start" minHeight={{ xs: 36, sm: 48 }}>
                {statistics.trend === 'up' && <TrendingUpIcon sx={{ fontSize: { xs: 36, sm: 48 }, color: 'success.main' }} />}
                {statistics.trend === 'down' && <TrendingDownIcon sx={{ fontSize: { xs: 36, sm: 48 }, color: 'error.main' }} />}
                {statistics.trend === 'stable' && <TrendingFlatIcon sx={{ fontSize: { xs: 36, sm: 48 }, color: 'grey.500' }} />}
                {statistics.trend === null && <Typography sx={{ fontSize: { xs: '1.5rem', sm: '2.125rem' }, fontWeight: 700 }}>-</Typography>}
              </Box>
            </CardContent>
          </Card>
        </Grid>
        {!isMobile && (
          <Grid item xs={4} sm={4} md={3}>
            <Card sx={{ height: '100%', '&:hover': { transform: 'translateY(-2px)', boxShadow: 6 } }}>
              <CardContent sx={{ p: { xs: 1.5, sm: 2 } }}>
                <Typography color="text.secondary" gutterBottom sx={{ fontSize: { xs: '0.7rem', sm: '0.875rem' } }}>
                  Letzter Score
                </Typography>
                <Typography sx={{ fontSize: { xs: '1.5rem', sm: '2.125rem' }, fontWeight: 700 }}>
                  {statistics.latestScore !== null ? statistics.latestScore : '-'}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        )}
      </Grid>

      {/* Combined Timeline Chart - Shows correlation between LLM tests and Optimization tests */}
      {combinedTimeline && combinedTimeline.items.length > 0 && (
        <Box mb={3}>
          <CombinedTimelineChart data={combinedTimeline} />
        </Box>
      )}

      {/* Tests Table */}
      <Card>
        <CardContent sx={{ p: { xs: 1.5, sm: 2 } }}>
          <Typography variant="h6" gutterBottom>
            Test-Verlauf
          </Typography>
          <TableContainer component={Paper} sx={{ overflowX: 'auto' }}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>#</TableCell>
                  <TableCell>Datum</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Overall Score</TableCell>
                  <TableCell>Trend</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {(showAllTests ? tests : tests.slice(0, 3)).map((test) => {
                  const previousTest = tests[tests.indexOf(test) + 1];
                  const isRunning = test.status === 'running' || test.status === 'pending';
                  const isSelected = selectedTestId === test.id;
                  const isClickable = test.status === 'completed' && test.overall_score !== null;

                  return (
                    <TableRow
                      key={test.id}
                      onClick={() => isClickable && setSelectedTestId(test.id)}
                      sx={{
                        cursor: isClickable ? 'pointer' : 'default',
                        backgroundColor: isSelected ? 'action.selected' : 'inherit',
                        '&:hover': isClickable ? {
                          backgroundColor: isSelected ? 'action.selected' : 'action.hover',
                        } : {},
                      }}
                    >
                      <TableCell>#{tests.length - tests.indexOf(test)}</TableCell>
                      <TableCell>{formatDate(test.created_at)}</TableCell>
                      <TableCell>
                        {test.status === 'failed' && test.error_message ? (
                          <MuiTooltip title={safeText(test.error_message)} arrow>
                            <Chip
                              label={translateStatus(test.status)}
                              color={getStatusColor(test.status) as any}
                              size="small"
                            />
                          </MuiTooltip>
                        ) : (
                          <Chip
                            label={translateStatus(test.status)}
                            color={getStatusColor(test.status) as any}
                            size="small"
                          />
                        )}
                        {isRunning && (
                          <Box sx={{ mt: 1 }}>
                            <Typography variant="caption" display="block">
                              {test.progress_message || 'Analysiere...'}
                            </Typography>
                            {test.progress_current != null && test.progress_total != null && test.progress_total > 0 ? (
                              <LinearProgress
                                variant="determinate"
                                value={(test.progress_current / test.progress_total) * 100}
                                sx={{ mt: 0.5 }}
                              />
                            ) : (
                              <LinearProgress sx={{ mt: 0.5 }} />
                            )}
                          </Box>
                        )}
                      </TableCell>
                      <TableCell>
                        {test.overall_score !== null ? test.overall_score : '-'}
                      </TableCell>
                      <TableCell>
                        {getTrendIcon(test.overall_score, previousTest?.overall_score || null)}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
          {tests.length > 3 && (
            <Box display="flex" justifyContent="center" mt={1}>
              <IconButton
                size="small"
                onClick={() => setShowAllTests(!showAllTests)}
                sx={{ color: 'text.secondary' }}
              >
                {showAllTests ? <ExpandLessIcon /> : <ExpandMoreIcon />}
              </IconButton>
            </Box>
          )}
        </CardContent>
      </Card>

      {/* Detailed Criteria Breakdown - Show for selected test */}
      {(() => {
        if (!tests || tests.length === 0 || !selectedTestId) return null;

        const selectedTest = tests.find(t => t.id === selectedTestId);
        if (!selectedTest || selectedTest.status !== 'completed' || !selectedTest.overall_score) return null;

        // Calculate test number (tests are ordered newest first)
        const testNumber = tests.length - tests.findIndex(t => t.id === selectedTestId);

        const v2Icons: Record<V2CategoryKey, React.ReactNode> = {
          entity: <AccountBalanceIcon />,
          content: <DescriptionIcon />,
          reputation: <StarIcon />,
          usp: <EmojiObjectsIcon />,
          local: <PlaceIcon />,
          thirdparty: <SourceIcon />,
          technical: <CodeIcon />,
        };

        return selectedTest.analysis_result ? (
          <Box mt={3}>
            <Alert severity="warning" sx={{ mb: 3 }}>
              <Typography variant="body2" fontWeight={600} gutterBottom>
                Hinweis: KI-generierte Analyse und Empfehlungen
              </Typography>
              <Typography variant="body2">
                Die folgende Analyse sowie sämtliche Empfehlungen wurden automatisiert
                von einer KI erstellt und können Fehler enthalten. Vorschläge können
                rechtliche Implikationen haben (u. a. Datenschutz/DSGVO, Wettbewerbs-
                und Werberecht, Urheberrecht, Kennzeichnungspflichten z. B. bei
                Kundenstimmen oder Testimonials). Bitte vor der Umsetzung inhaltlich
                und rechtlich prüfen (lassen).
              </Typography>
            </Alert>
            {/* Top Recommendations + Radar Chart (V2 only) */}
            {isV2Test(selectedTest) && (selectedTest.analysis_result as AnalysisResultV2)?.top_recommendations?.length > 0 && (
              <Grid container spacing={3} mb={3}>
                <Grid item xs={12} md={6}>
                  <TopRecommendationsCard
                    recommendations={(selectedTest.analysis_result as AnalysisResultV2).top_recommendations}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <OptimizationRadarChart test={selectedTest} />
                </Grid>
              </Grid>
            )}

            <Card>
              <CardContent>
                <Box display="flex" alignItems="center" justifyContent="space-between" mb={1}>
                  <Typography variant="h6" gutterBottom>
                    {isV2Test(selectedTest)
                      ? `7 Kategorien Analyse zu Test #${testNumber}`
                      : `Detaillierte Kriterien-Analyse zu Test #${testNumber}`}
                  </Typography>
                  {isV2Test(selectedTest) && selectedTest.overall_score !== null && (
                    <Chip
                      label={`${selectedTest.overall_score} - ${getScoreLabel(selectedTest.overall_score)}`}
                      sx={{
                        backgroundColor: getScoreColor(selectedTest.overall_score),
                        color: 'white',
                        fontWeight: 600,
                      }}
                    />
                  )}
                </Box>
                <Box mt={2}>
                  {isV2Test(selectedTest) ? (
                    // V2: Dynamic rendering of 7 AI-Visibility categories
                    <>
                      {CATEGORY_ORDER.map((key) => {
                        const catConfig = CATEGORY_CONFIG[key];
                        const result = selectedTest.analysis_result as AnalysisResultV2;
                        const catData = result?.[key] as CategoryResultV2 | undefined;
                        if (!catData) return null;

                        return (
                          <CriterionDetailCard
                            key={key}
                            name={catConfig.name}
                            icon={v2Icons[key]}
                            data={catData}
                            scoringVersion={2}
                            expanded={expandedPanels.has(key)}
                            onToggle={() => togglePanel(key)}
                            projectId={projectId}
                            testId={selectedTestId!}
                            criterionKey={key}
                          />
                        );
                      })}
                    </>
                  ) : (
                    // V1: Legacy 6 hardcoded criteria
                    <>
                      {(() => {
                        const result = selectedTest.analysis_result as any;
                        const v1Criteria = [
                          { key: 'renewable_energy', name: 'Echter Ökostrom / Regionalität', icon: <NatureIcon /> },
                          { key: 'customer_satisfaction', name: 'Kundenzufriedenheit & Bewertungen', icon: <StarIcon /> },
                          { key: 'pricing_transparency', name: 'Transparente Preisstruktur', icon: <AttachMoneyIcon /> },
                          { key: 'local_partnerships', name: 'Kommunale/Lokale Partnerschaften', icon: <HandshakeIcon /> },
                          { key: 'web_visibility', name: 'Web-Sichtbarkeit & Datenstruktur', icon: <VisibilityIcon /> },
                          { key: 'sustainability_communication', name: 'Nachhaltigkeitskommunikation', icon: <CampaignIcon /> },
                        ];
                        return v1Criteria.map(({ key, name: criterionName, icon: criterionIcon }) => {
                          const catData = result?.[key];
                          if (!catData) return null;
                          return (
                            <CriterionDetailCard
                              key={key}
                              name={criterionName}
                              icon={criterionIcon}
                              data={catData}
                              expanded={expandedPanels.has(key)}
                              onToggle={() => togglePanel(key)}
                              projectId={projectId}
                              testId={selectedTestId!}
                              criterionKey={key}
                            />
                          );
                        });
                      })()}
                    </>
                  )}
                </Box>

                {/* Grounding Sources Section */}
                {(selectedTest.analysis_result as any)?._grounding_metadata && (
                  <>
                    <Divider sx={{ my: 3 }} />
                    <Box>
                      <Accordion
                        expanded={expandedPanels.has('grounding_sources')}
                        onChange={() => togglePanel('grounding_sources')}
                        sx={{
                          boxShadow: 'none',
                          border: '1px solid',
                          borderColor: 'divider',
                          '&:before': { display: 'none' },
                        }}
                      >
                        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                          <Box display="flex" alignItems="center" gap={1}>
                            <SourceIcon color="primary" />
                            <Typography variant="subtitle1" fontWeight={600}>
                              Analysierte Quellen
                            </Typography>
                            <Chip
                              size="small"
                              label={(() => {
                                const sources = (selectedTest.analysis_result as any)?._grounding_metadata?.sources || [];
                                // Group by title (which contains the actual domain like "swm.de")
                                const uniqueDomains = new Set(sources.map((s: { title?: string; url?: string }) => s.title || s.url));
                                return `${uniqueDomains.size} Quellen`;
                              })()}
                              color="primary"
                              variant="outlined"
                            />
                          </Box>
                        </AccordionSummary>
                        <AccordionDetails>
                          {/* Search Queries */}
                          {(selectedTest.analysis_result as any)._grounding_metadata.search_queries &&
                           (selectedTest.analysis_result as any)._grounding_metadata.search_queries.length > 0 && (
                            <Box mb={3}>
                              <Typography variant="subtitle2" color="text.secondary" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                <SearchIcon fontSize="small" />
                                Durchgeführte Suchanfragen
                              </Typography>
                              <Box display="flex" flexWrap="wrap" gap={1}>
                                {(selectedTest.analysis_result as any)._grounding_metadata.search_queries.map((query: string, idx: number) => (
                                  <Chip
                                    key={idx}
                                    label={safeText(query)}
                                    size="small"
                                    variant="outlined"
                                    sx={{ maxWidth: '100%' }}
                                  />
                                ))}
                              </Box>
                            </Box>
                          )}

                          {/* Sources List - Grouped by Domain */}
                          {(selectedTest.analysis_result as any)._grounding_metadata.sources &&
                           (selectedTest.analysis_result as any)._grounding_metadata.sources.length > 0 ? (
                            <Box>
                              <Typography variant="subtitle2" color="text.secondary" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                <SourceIcon fontSize="small" />
                                Gesichtete Webseiten
                              </Typography>
                              <Box component="ul" sx={{ pl: 2, m: 0 }}>
                                {(() => {
                                  // Group sources by title (which contains the actual domain like "swm.de")
                                  const sourcesByTitle = (selectedTest.analysis_result as any)!._grounding_metadata!.sources.reduce((acc: Record<string, { urls: string[] }>, source: { title?: string; url: string }) => {
                                    const key = source.title || source.url;
                                    if (!acc[key]) {
                                      acc[key] = { urls: [] as string[] };
                                    }
                                    acc[key].urls.push(source.url);
                                    return acc;
                                  }, {} as Record<string, { urls: string[] }>);

                                  return (Object.entries(sourcesByTitle) as [string, { urls: string[] }][]).map(([title, { urls }]) => (
                                    <Box
                                      component="li"
                                      key={title}
                                      sx={{ mb: 1 }}
                                    >
                                      <Box display="flex" alignItems="center" gap={1}>
                                        <Link
                                          href={urls[0]}
                                          target="_blank"
                                          rel="noopener noreferrer"
                                          sx={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: 0.5,
                                            textDecoration: 'none',
                                            '&:hover': { textDecoration: 'underline' },
                                          }}
                                        >
                                          {title}
                                          <OpenInNewIcon fontSize="small" sx={{ fontSize: 14, opacity: 0.7 }} />
                                        </Link>
                                        {urls.length > 1 && (
                                          <MuiTooltip
                                            title={
                                              <Box sx={{ maxWidth: 400 }}>
                                                {urls.map((url: string, idx: number) => (
                                                  <Box key={idx} sx={{ mb: 0.5, wordBreak: 'break-all' }}>
                                                    <Link
                                                      href={url}
                                                      target="_blank"
                                                      rel="noopener noreferrer"
                                                      sx={{ color: 'inherit', fontSize: '0.75rem' }}
                                                    >
                                                      {url}
                                                    </Link>
                                                  </Box>
                                                ))}
                                              </Box>
                                            }
                                            arrow
                                            placement="right"
                                          >
                                            <Chip
                                              size="small"
                                              label={`${urls.length} Seiten`}
                                              sx={{ height: 20, fontSize: '0.7rem', cursor: 'pointer' }}
                                            />
                                          </MuiTooltip>
                                        )}
                                      </Box>
                                    </Box>
                                  ));
                                })()}
                              </Box>
                            </Box>
                          ) : (
                            <Typography variant="body2" color="text.secondary">
                              Keine Quellen verfügbar
                            </Typography>
                          )}
                        </AccordionDetails>
                      </Accordion>
                    </Box>
                  </>
                )}
              </CardContent>
            </Card>
          </Box>
        ) : null;
      })()}
    </Box>
  );
});

OptimizationTab.displayName = 'OptimizationTab';

export default OptimizationTab;
